import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useEvent } from '../contexts/EventContext';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Trophy, ArrowLeft, Car as CarIcon, CheckCircle, Heart } from 'lucide-react';

export default function PublicVoting() {
  const { currentEvent } = useEvent();
  const [searchParams] = useSearchParams();
  const preselectedCarId = searchParams.get('car');
  
  const [votingCategories, setVotingCategories] = useState([]);
  const [cars, setCars] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCar, setSelectedCar] = useState(preselectedCarId || '');
  const [selectedCarPhoto, setSelectedCarPhoto] = useState(null);
  const [voterEmail, setVoterEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [voted, setVoted] = useState(false);

  useEffect(() => {
    if (currentEvent) {
      fetchData();
    }
  }, [currentEvent]);

  // When the user picks a car, fetch just that car's full doc so we can
  // show the photo thumbnail. The initial /cars list is loaded with
  // include_photos=false to keep the voting page lightweight (otherwise
  // every voter downloads every approved car's base64 photo payload).
  useEffect(() => {
    if (!selectedCar) {
      setSelectedCarPhoto(null);
      return;
    }
    const existing = cars.find(c => c.id === selectedCar);
    if (existing && existing.photo_data) {
      setSelectedCarPhoto(existing.photo_data);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/cars/${selectedCar}`);
        if (!cancelled) setSelectedCarPhoto(res.data?.photo_data || null);
      } catch (_e) {
        if (!cancelled) setSelectedCarPhoto(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCar, cars]);

  const fetchData = async () => {
    if (!currentEvent) return;

    // Fire the two requests independently so a slow/failed cars response
    // doesn't blank out the voting categories (which is what caused the
    // "Voting is not available yet" flash on production).
    try {
      const categoriesRes = await axios.get(`${API}/voting-categories?event_id=${currentEvent.id}`);
      setVotingCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching voting categories:', error);
    }

    try {
      // include_photos=false drops the base64 photo_data from the list —
      // this endpoint was returning 100s of MB during the live show as
      // judges uploaded photos, causing the voting page to time out.
      const carsRes = await axios.get(
        `${API}/cars?approved_only=true&event_id=${currentEvent.id}&include_photos=false`
      );
      setCars(carsRes.data);
    } catch (error) {
      console.error('Error fetching cars:', error);
      toast.error('Failed to load cars');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/votes`, {
        voter_email: voterEmail,
        car_id: selectedCar,
        voting_category_id: selectedCategory,
        marketing_consent: marketingConsent
      });
      toast.success('Vote submitted successfully!');
      setVoted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit vote');
    } finally {
      setLoading(false);
    }
  };

  const selectedCarData = cars.find(c => c.id === selectedCar);

  if (!currentEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  if (voted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-gray-50">
        <div className="bg-white border border-gray-200 shadow-lg rounded-xl p-12 text-center max-w-md">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4 text-gray-900">Thank You!</h2>
          <p className="text-gray-600 mb-8">
            Your vote has been recorded successfully.
          </p>
          <div className="space-y-3">
            <Link to="/gallery">
              <Button className="w-full bg-pink-600 hover:bg-pink-700 text-white">
                View All Cars
              </Button>
            </Link>
            <Link to="/leaderboard">
              <Button variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50">
                View Leaderboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 bg-gray-50" data-testid="public-voting-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/">
              <Button variant="ghost" className="text-gray-700 hover:text-pink-600">
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back
              </Button>
            </Link>
            <Logo size="sm" showText={true} />
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 mt-12">
        <div className="bg-white border border-gray-200 shadow-lg rounded-xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-8 h-8 text-pink-500" />
            <h2 className="text-4xl font-bold text-gray-900">
              People's Choice
            </h2>
          </div>
          <p className="text-gray-600 mb-8">
            Vote for your favorite car in {currentEvent.name}
          </p>

          {votingCategories.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
              <Trophy className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <p className="text-xl text-gray-600 font-medium">Voting is not available yet</p>
              <p className="text-gray-500 mt-2">Check back later when voting categories are set up</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="voting-form">
              <div>
                <Label htmlFor="email" className="text-gray-700">Your Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={voterEmail}
                  onChange={(e) => setVoterEmail(e.target.value)}
                  className="bg-white border-gray-300 text-gray-900 mt-2"
                  placeholder="your@email.com"
                  required
                  data-testid="voter-email-input"
                />
                <p className="text-sm text-gray-500 mt-2">
                  One vote per email address per category
                </p>
              </div>

              <div>
                <Label htmlFor="category" className="text-gray-700">Voting Category *</Label>
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                  required
                >
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900 mt-2" data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {votingCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id} className="text-gray-900">
                        {cat.name}{cat.description ? ` - ${cat.description}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="car" className="text-gray-700">Select Car *</Label>
                <Select
                  value={selectedCar}
                  onValueChange={setSelectedCar}
                  required
                >
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900 mt-2" data-testid="car-select">
                    <SelectValue placeholder="Choose a car to vote for" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-60">
                    {cars.map((car) => (
                      <SelectItem key={car.id} value={car.id} className="text-gray-900">
                        #{car.registration_number} - {car.year} {car.make} {car.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCarData && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="selected-car-preview">
                  <h4 className="text-lg font-semibold mb-4 text-gray-900">Your Selection:</h4>
                  <div className="flex items-center space-x-4">
                    {selectedCarPhoto ? (
                      <img
                        src={selectedCarPhoto}
                        alt={`${selectedCarData.make} ${selectedCarData.model}`}
                        className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                        <CarIcon className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedCarData.make} {selectedCarData.model}
                      </p>
                      <p className="text-gray-600">
                        {selectedCarData.year} • {selectedCarData.class_name}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Registration #{selectedCarData.registration_number}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Marketing Consent */}
              <div className="flex items-start space-x-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <Checkbox 
                  id="marketing-consent" 
                  checked={marketingConsent}
                  onCheckedChange={setMarketingConsent}
                  className="mt-0.5 border-gray-400 data-[state=checked]:bg-pink-600 data-[state=checked]:border-pink-600"
                  data-testid="marketing-consent-checkbox"
                />
                <label 
                  htmlFor="marketing-consent" 
                  className="text-sm text-gray-800 cursor-pointer select-none leading-relaxed"
                >
                  I agree to receive occasional emails from Cruise for a Cause about future events, 
                  news, and promotions. You can unsubscribe at any time.
                </label>
              </div>

              <Button
                type="submit"
                className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6 text-lg font-semibold"
                disabled={loading || !selectedCategory || !selectedCar || !voterEmail}
                data-testid="submit-vote-btn"
              >
                {loading ? 'Submitting...' : 'Cast Your Vote'}
              </Button>
            </form>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-blue-700 text-sm">
            <strong>How it works:</strong> Each email can vote once per category. 
            Winners will be announced at the end of the event!
          </p>
        </div>
      </div>
    </div>
  );
}
